import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { Trip, TripDirection, TransportRoute } from 'contracts/transport-routes';
import { useTranslation } from 'hooks';
import { Card, CardContent } from 'components/ui/card';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, InputGs } from 'components/ui/Forms';
import { cn } from 'lib/utils';
import { useSelectedGeneralCompanyState } from 'pages/Settings/transport-routes/hooks';

import {
  TripStopsManager,
  type TripStopsManagerRef,
} from '../trip-stops-manager';
import { TollsPreview } from '../tolls-preview';
import { useTripManagerState } from './hooks';
import { translations } from './translations';
import { formSchema, FormSchema } from './form-schema';

type TripData = TransportRoute['forward_trip' | 'backward_trip'];

type Ref = {
  getFormData: () => Promise<Trip>;
  setTripData: (trip: TripData) => void;
};

type Props = React.HTMLAttributes<HTMLDivElement> & {
  title?: React.ReactNode;
  tripDirection: TripDirection;
};

export type TripManagerRef = Ref;

export const TripManager = React.forwardRef<Ref, Props>(({
                                                           title,
                                                           tripDirection,
                                                           className,
                                                           ...props
                                                         }, ref) => {
  const { getTranslation: t } = useTranslation(translations);
  const { selectedGeneralCompany } = useSelectedGeneralCompanyState();
  const lastAppliedCenterKeyRef = React.useRef<string | null>(null);

  const tripStopsManagerRef = React.useRef<TripStopsManagerRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const {
    originCityOptions,
    destinationCityOptions,
    ufOptions,
    onOriginUfChange,
    onOriginIbgeChange,
    onDestinationUfChange,
    onDestinationIbgeChange,
    onOriginLatChange,
    onOriginLngChange,
    onDestinationLatChange,
    onDestinationLngChange,
  } = useTripManagerState({ tripDirection: tripDirection, form });

  const onReceiveTripData = React.useCallback((trip: TripData) => {
    const { origin, destination } = trip;

    if (origin) {
      form.setValue('origin_uf', origin.uf);
      onOriginUfChange(origin.uf, true);
      form.setValue('origin_ibge', origin.ibge);
      onOriginIbgeChange(origin.ibge, true);

      form.setValue('origin_lat', origin.lat);
      form.setValue('origin_lng', origin.lng);
      onOriginLatChange(origin.lat);
      onOriginLngChange(origin.lng);
    }

    if (destination) {
      form.setValue('destination_uf', destination.uf);
      onDestinationUfChange(destination.uf, true);
      form.setValue('destination_ibge', destination.ibge);
      onDestinationIbgeChange(destination.ibge, true);

      form.setValue('destination_lat', destination.lat);
      form.setValue('destination_lng', destination.lng);
      onDestinationLatChange(destination.lat);
      onDestinationLngChange(destination.lng);
    }

    tripStopsManagerRef.current?.setStopLocations(trip.stops);
  }, [
    form,
    onOriginUfChange,
    onDestinationUfChange,
    onOriginIbgeChange,
    onDestinationIbgeChange,
    onOriginLatChange,
    onOriginLngChange,
    onDestinationLatChange,
    onDestinationLngChange,
  ]);

  const getFormValues = React.useCallback(async (): Promise<FormSchema> => {
    const isFormValid = await form.trigger();

    if (!isFormValid) {
      throw new Error('Trip manager form has invalid data');
    }

    return form.getValues();
  }, [form]);

  const getFormData = React.useCallback(async (): Promise<Trip> => {
    const formValues = await getFormValues();

    return {
      origin: {
        ibge: formValues.origin_ibge,
        uf: formValues.origin_uf,
        lat: formValues.origin_lat,
        lng: formValues.origin_lng,
      },
      destination: {
        ibge: formValues.destination_ibge,
        uf: formValues.destination_uf,
        lat: formValues.destination_lat,
        lng: formValues.destination_lng,
      },
      stops: tripStopsManagerRef.current?.getStopLocations() || [],
    };
  }, [getFormValues]);

  React.useImperativeHandle(ref, () => ({
    getFormData,
    setTripData: onReceiveTripData,
  }), [getFormData, onReceiveTripData]);

  React.useEffect(() => {
    const centerUf = selectedGeneralCompany?.address_state;
    const centerIbge = selectedGeneralCompany?.address_id_city_ibge;
    const centerLat = selectedGeneralCompany?.address_latitude;
    const centerLng = selectedGeneralCompany?.address_longitude;
    const centerId = selectedGeneralCompany?.id;

    if (!centerId || !centerUf || !centerIbge || !centerLat || !centerLng) return;

    const centerKey = `${tripDirection}-${centerId}`;
    if (lastAppliedCenterKeyRef.current === centerKey) {
      return;
    }
    lastAppliedCenterKeyRef.current = centerKey;

    const targetPrefix = tripDirection === 'backward'
      ? 'destination'
      : 'origin';

    if (targetPrefix === 'origin') {
      const currentUf = form.getValues('origin_uf');
      const currentIbge = form.getValues('origin_ibge');

      if (currentUf !== centerUf) {
        form.setValue('origin_uf', centerUf);
        onOriginUfChange(centerUf, true);
      }

      if (currentIbge !== centerIbge) {
        form.setValue('origin_ibge', centerIbge);
        onOriginIbgeChange(centerIbge, true);
      }

      form.setValue('origin_lat', centerLat);
      form.setValue('origin_lng', centerLng);
      onOriginLatChange(centerLat);
      onOriginLngChange(centerLng);
      return;
    }

    const currentUf = form.getValues('destination_uf');
    const currentIbge = form.getValues('destination_ibge');

    if (currentUf !== centerUf) {
      form.setValue('destination_uf', centerUf);
      onDestinationUfChange(centerUf, true);
    }

    if (currentIbge !== centerIbge) {
      form.setValue('destination_ibge', centerIbge);
      onDestinationIbgeChange(centerIbge, true);
    }

    form.setValue('destination_lat', centerLat);
    form.setValue('destination_lng', centerLng);
    onDestinationLatChange(centerLat);
    onDestinationLngChange(centerLng);
  }, [
    form,
    selectedGeneralCompany,
    tripDirection,
    onOriginUfChange,
    onOriginIbgeChange,
    onOriginLatChange,
    onOriginLngChange,
    onDestinationUfChange,
    onDestinationIbgeChange,
    onDestinationLatChange,
    onDestinationLngChange,
  ]);

  return (
    <div {...props} className={cn('w-full space-y-6', className)}>
      {title && <div className={'text-gray-600 font-GilroyBold text-[18px]'}>
        {title}
      </div>}
      <Card className={'bg-gray-200'}>
        <CardContent className={'pt-6'}>
          <Card>
            <CardContent className={'p-6 bg-white'}>
              <Form {...form}>
                <form>
                  <div className={'grid grid-cols-4 gap-4'}>
                    <FormField
                      control={form.control}
                      name={'origin_uf'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('originUf')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={ufOptions.singleSelectOptions}
                          onChange={onOriginUfChange}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={'origin_ibge'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('originCity')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={originCityOptions}
                          onChange={onOriginIbgeChange}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={'destination_uf'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('destinationUf')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={ufOptions.singleSelectOptions}
                          onChange={onDestinationUfChange}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={'destination_ibge'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('destinationCity')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={destinationCityOptions}
                          onChange={onDestinationIbgeChange}
                        />
                      )}
                    />
                    <FormField
                      name={'origin_lat'}
                      control={form.control}
                      render={({ field }) => (
                        <InputGs
                          {...field}
                          label={t('originLatitude')}
                          onChange={(event) => {
                            field.onChange(event);
                            onOriginLatChange(event.target.value);
                          }}
                        />
                      )}
                    />
                    <FormField
                      name={'origin_lng'}
                      control={form.control}
                      render={({ field }) => (
                        <InputGs
                          {...field}
                          label={t('originLongitude')}
                          onChange={(event) => {
                            field.onChange(event);
                            onOriginLngChange(event.target.value);
                          }}
                        />
                      )}
                    />
                    <FormField
                      name={'destination_lat'}
                      control={form.control}
                      render={({ field }) => (
                        <InputGs
                          {...field}
                          label={t('destinationLatitude')}
                          onChange={(event) => {
                            field.onChange(event);
                            onDestinationLatChange(event.target.value);
                          }}
                        />
                      )}
                    />
                    <FormField
                      name={'destination_lng'}
                      control={form.control}
                      render={({ field }) => (
                        <InputGs
                          {...field}
                          label={t('destinationLongitude')}
                          onChange={(event) => {
                            field.onChange(event);
                            onDestinationLngChange(event.target.value);
                          }}
                        />
                      )}
                    />
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
          <TripStopsManager
            ref={tripStopsManagerRef}
            tripDirection={tripDirection}
          />
          <TollsPreview
            tripDirection={tripDirection}
            className={'mt-6'}
          />
        </CardContent>
      </Card>
    </div>
  );
});
TripManager.displayName = 'TripManager';